import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Table } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferGet from 'api/interfaces/offers/IOfferGet';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import translations from 'services/translations/translations';
import useDateLocale from 'services/translations/useDateLocale';

/**
 * The offers a company or a contact person is linked to, in any role (customer, designer,
 * recipient…), with the main information of the project. A row opens the offer. Hidden
 * from the users who cannot read offers.
 */
const LinkedOffers = ({
  contactId,
  contactPersonId,
}: {
  contactId?: string;
  contactPersonId?: string;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.contacts, translations.offers]);
  const text = (key: string) =>
    t(`${translations.contacts}:linkedOffers.${key}`);
  const dateLocale = useDateLocale();
  const history = useHistory();

  const [offers, setOffers] = useState<IOfferGet[] | null>(null);

  useEffect(() => {
    apiOffers
      .get({
        limit: 200,
        linkedContactId: contactPersonId ? undefined : Number(contactId),
        linkedContactPersonId: contactPersonId
          ? Number(contactPersonId)
          : undefined,
        offset: 0,
        orderBy: 'updated',
        orderByDirection: 'desc',
      })
      .then(setOffers)
      .catch(() => setOffers(null));
  }, [contactId, contactPersonId]);

  if (offers === null) {
    return null;
  }

  const formatDate = (value: null | string | undefined) =>
    value ? new Date(value).toLocaleDateString(dateLocale.intl) : '';

  const address = (offer: IOfferGet) =>
    [
      offer.streetAddress,
      [offer.postalNumber, offer.city].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ') ||
    offer.address ||
    '';

  return (
    <div className="linked-offers">
      <h3>{text('Title')}</h3>
      {offers.length === 0 ? (
        <p>{text('None')}</p>
      ) : (
        <div className="table-scroll">
          <Table size="sm" hover className="clean-table linked-offers-table">
            <thead>
              <tr>
                <th>{text('Projects')}</th>
                <th>{text('Target')}</th>
                <th>{text('Address')}</th>
                <th>{text('Type')}</th>
                <th>{text('Status')}</th>
                <th>{text('Department')}</th>
                <th>{text('Customer')}</th>
                <th className="text-right">{text('Total')}</th>
                <th>{text('Updated')}</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => (
                <tr
                  key={`${offer.type}-${offer.id}`}
                  onClick={() =>
                    history.push(`/offers/${offer.id}/${offer.type}`)
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <td>{offer.projectNumbers}</td>
                  <td>{offer.targetName}</td>
                  <td>{address(offer)}</td>
                  <td>{t(`${translations.offers}:types.${offer.type}`)}</td>
                  <td>
                    {getOfferStatusLabel(t, offer.status ?? '', offer.type)}
                  </td>
                  <td>{offer.organizationUnitName}</td>
                  <td>{offer.customerName}</td>
                  <td className="text-right text-nowrap">
                    {offer.totalPrice == null
                      ? ''
                      : `${offer.totalPrice.toLocaleString(dateLocale.intl, {
                          maximumFractionDigits: 0,
                        })} €`}
                  </td>
                  <td className="text-nowrap">{formatDate(offer.updated)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default LinkedOffers;
